import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { supabase } from "../../lib/supabase";
import {
  AdminTable,
  type AdminTableColumn,
  FilterToggle,
  Pagination,
  PageHeader,
  SearchBar,
  useDebouncedValue,
} from "../../components/admin/adminPrimitives";
import { INTERNAL_EMAIL_SUFFIX } from "../../components/admin/isAdmin";

interface UserRow {
  id: string;
  school_name: string;
  email: string;
  full_name: string | null;
  created_at: string;
  licences_count: number;
}

const PAGE_SIZE = 25;

export function Users() {
  const [rows, setRows] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [error, setError] = useState<string | null>(null);
  const [showInternal, setShowInternal] = useState(false);

  useEffect(() => {
    // Any time the search query changes, jump back to page 0 so the
    // user doesn't land on an out-of-range page with no results.
    setPage(0);
  }, [debouncedSearch, showInternal]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);

      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      // Left-join licences by selecting the relation as a count. Supabase
      // returns the count as an object under the relation key; we unwrap
      // it below.
      let query = supabase
        .from("accounts")
        .select(
          "id, school_name, email, full_name, created_at, licenses:licenses(count)",
          { count: "exact" },
        )
        .order("created_at", { ascending: false })
        .range(from, to);

      if (!showInternal) {
        query = query.not("email", "ilike", `%${INTERNAL_EMAIL_SUFFIX}`);
      }

      const q = debouncedSearch.trim();
      if (q) {
        // Supabase .or() with ilike — escape quotes/backslashes so a
        // school name with an apostrophe doesn't break the filter.
        const safe = q.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
        query = query.or(
          `school_name.ilike.%${safe}%,email.ilike.%${safe}%,full_name.ilike.%${safe}%`,
        );
      }

      const { data, count, error: queryError } = await query;

      if (cancelled) return;
      if (queryError) {
        console.error("Users fetch error:", queryError);
        setError("Failed to load users: " + queryError.message);
        setRows([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      setRows(
        (data ?? []).map((r: Record<string, unknown>) => ({
          id: r.id as string,
          school_name: (r.school_name as string) ?? "",
          email: (r.email as string) ?? "",
          full_name: (r.full_name as string | null) ?? null,
          created_at: r.created_at as string,
          licences_count:
            ((r.licenses as { count: number }[] | undefined)?.[0]?.count ?? 0) as number,
        })),
      );
      setTotal(count ?? 0);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, showInternal]);

  const columns: AdminTableColumn<UserRow>[] = [
    {
      key: "school",
      header: "School",
      render: (row) => (
        <Link
          to={`/admin/users/${row.id}`}
          className="font-medium text-forest-900 hover:underline"
        >
          {row.school_name || "(unnamed)"}
        </Link>
      ),
    },
    {
      key: "name",
      header: "Contact",
      render: (row) => (
        <div className="text-forest-600">
          <div>{row.full_name ?? "\u2014"}</div>
          <div className="text-xs text-forest-500">{row.email}</div>
        </div>
      ),
    },
    {
      key: "licences",
      header: "Licences",
      render: (row) => (
        <span className="tabular-nums text-forest-700">{row.licences_count}</span>
      ),
    },
    {
      key: "joined",
      header: "Joined",
      render: (row) => (
        <span className="text-forest-500">
          {new Date(row.created_at).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      key: "action",
      header: "",
      align: "right",
      render: (row) => (
        <Link
          to={`/admin/users/${row.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-forest-700 hover:text-forest-900"
        >
          Open
          <ArrowRightIcon className="h-3.5 w-3.5" />
        </Link>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Users"
        description="Every school with a Bookburrow account. Click a row to see licences, activations, and admin actions."
        actions={
          <>
            <FilterToggle
              label="Show internal users"
              checked={showInternal}
              onChange={setShowInternal}
            />
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search school, email, or contact"
            />
          </>
        }
      />

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <AdminTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyMessage={
          debouncedSearch
            ? `No users match "${debouncedSearch}".`
            : "No users yet."
        }
      />

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />
    </div>
  );
}
